import React from 'react';
import type { CriteriaResult } from '../../types/mysteryShop';
import { getScoreColor } from './utils';

// ── Score Badge ────────────────────────────────────────────────────────────────

type SizeType = 'sm' | 'md' | 'lg';

interface ScoreBadgeProps {
  score: number;
  size?: SizeType;
}

export const ScoreBadge: React.FC<ScoreBadgeProps> = ({ score, size = 'md' }) => {
  const sizeClasses = {
    sm: { container: 'w-10 h-10', text: 'text-sm' },
    md: { container: 'w-14 h-14', text: 'text-lg' },
    lg: { container: 'w-20 h-20', text: 'text-2xl' }
  };
  return (
    <div
      className={`${sizeClasses[size].container} rounded-lg flex items-center justify-center font-bold ${sizeClasses[size].text}`}
      style={{ backgroundColor: getScoreColor(score), color: 'white' }}
    >
      {score}
    </div>
  );
};

// ── Tour Badge ─────────────────────────────────────────────────────────────────

interface TourBadgeProps {
  invited: boolean;
}

export const TourBadge: React.FC<TourBadgeProps> = ({ invited }) => (
  <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
    invited ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
  }`}>
    {invited ? '✓' : '✗'} Tour {invited ? 'Invited' : 'Not Invited'}
  </span>
);

// ── Stat Card ──────────────────────────────────────────────────────────────────

interface StatCardProps {
  label: string;
  value: string | number;
  subValue?: string;
  trend?: number;
  color?: string;
}

export const StatCard: React.FC<StatCardProps> = ({ label, value, subValue, trend, color }) => (
  <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
    <p className="text-gray-500 text-sm font-medium mb-1">{label}</p>
    <p className="text-3xl font-bold" style={{ color: color || '#1f2937' }}>{value}</p>
    {subValue && <p className="text-gray-400 text-sm mt-1">{subValue}</p>}
    {trend !== undefined && (
      <p className={`text-sm mt-2 font-medium ${trend >= 0 ? 'text-green-600' : 'text-red-600'}`}>
        {trend >= 0 ? '↑' : '↓'} {Math.abs(trend).toFixed(1)} vs last month
      </p>
    )}
  </div>
);

// ── Score Category (with private CriteriaItem) ─────────────────────────────────

const CriteriaItem: React.FC<{ text: string; passed: boolean }> = ({ text, passed }) => (
  <div className="flex items-start gap-2 py-1">
    <span className={`mt-0.5 ${passed ? 'text-green-500' : 'text-red-400'}`}>
      {passed ? '✓' : '✗'}
    </span>
    <span className={passed ? 'text-gray-700' : 'text-gray-400'}>{text}</span>
  </div>
);

interface ScoreCategoryProps {
  title: string;
  score: number;
  max: number;
  weight: number;
  criteria?: CriteriaResult[];
}

export const ScoreCategory: React.FC<ScoreCategoryProps> = ({ title, score, max, weight, criteria }) => (
  <div className="bg-gray-50 rounded-lg p-4">
    <div className="flex items-center justify-between mb-3">
      <div>
        <h4 className="font-semibold text-gray-800 text-sm uppercase tracking-wide">{title}</h4>
        <p className="text-xs text-gray-400">{weight}% weight</p>
      </div>
      <div className="text-right">
        <span className="text-2xl font-bold" style={{ color: getScoreColor((score / max) * 100) }}>{score}</span>
        <span className="text-gray-400 text-lg">/{max}</span>
      </div>
    </div>
    {criteria && (
      <div className="border-t border-gray-200 pt-3 mt-2">
        {criteria.map((item, idx) => (
          <CriteriaItem key={idx} text={item.text} passed={item.passed} />
        ))}
      </div>
    )}
  </div>
);

// ── View Toolbar ───────────────────────────────────────────────────────────────

interface ViewToolbarProps {
  children?: React.ReactNode;
  onRefresh: () => void;
}

export const ViewToolbar: React.FC<ViewToolbarProps> = ({ children, onRefresh }) => (
  <div className="flex items-center justify-between mb-6">
    <div className="flex items-center gap-2">{children}</div>
    <button
      onClick={onRefresh}
      className="p-2 text-gray-400 hover:text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
      title="Refresh"
    >
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 2v6h-6"/>
        <path d="M3 12a9 9 0 0 1 15-6.7L21 8"/>
        <path d="M3 22v-6h6"/>
        <path d="M21 12a9 9 0 0 1-15 6.7L3 16"/>
      </svg>
    </button>
  </div>
);
